import { differenceInSeconds, subMinutes } from 'date-fns';
import { z } from 'zod';

import {
  DiscoveryFilters,
  DiscoveryNumericFilter,
  DiscoveryParametersDeserialized,
  DiscoveryParametersSerialized,
  DiscoveryPreset,
  DiscoveryTimeframe,
  filterableNumericColumns,
  zDiscoveryParameters,
  zSerializedDiscoveryParameters,
} from './types';

export function deserializeDiscoveryState(val: z.infer<typeof zSerializedDiscoveryParameters>) {
  const outFilters = {
    networks: val.networks ?? [],
    creator: val.creator,
    launchpads: val.launchpads ?? [],
    currencies: val.currencies ?? [],
    issuers: val.issuers ?? [],
    backings: val.backings ?? [],
  } as DiscoveryFilters;

  const out = {
    filters: outFilters,
    timeframe: val.timeframe ?? '24',
    sortBy: val.sortBy,
    sortDirection: val.sortDirection ?? 'desc',
    page: val.page ?? 1,
    preset: val.preset,
    pageDiscovery: val.pageDiscovery,
    searchQuery: val.searchQuery,
  };

  if (val.filterLiquidityMin !== undefined || val.filterLiquidityMax !== undefined) {
    out.filters.liquidity = {
      min: val.filterLiquidityMin,
      max: val.filterLiquidityMax,
    };
  }
  if (val.filterMarketCapMin !== undefined || val.filterMarketCapMax !== undefined) {
    out.filters.marketCap = {
      min: val.filterMarketCapMin,
      max: val.filterMarketCapMax,
    };
  }
  if (val.filterVolByTimeMin !== undefined || val.filterVolByTimeMax !== undefined) {
    out.filters.volByTime = {
      min: val.filterVolByTimeMin,
      max: val.filterVolByTimeMax,
    };
  }
  if (val.filterNumTxsByTimeMin !== undefined || val.filterNumTxsByTimeMax !== undefined) {
    out.filters.numTxsByTime = {
      min: val.filterNumTxsByTimeMin,
      max: val.filterNumTxsByTimeMax,
    };
  }
  if (val.filterAgeMin !== undefined || val.filterAgeMax !== undefined) {
    out.filters.age = {
      min: val.filterAgeMin,
      max: val.filterAgeMax,
    };
  }
  if (val.filterNumHoldersMin !== undefined || val.filterNumHoldersMax !== undefined) {
    out.filters.numHolders = {
      min: val.filterNumHoldersMin,
      max: val.filterNumHoldersMax,
    };
  }
  if (val.filterChange5mMin !== undefined || val.filterChange5mMax !== undefined) {
    out.filters.change5m = {
      min: val.filterChange5mMin,
      max: val.filterChange5mMax,
    };
  }
  if (val.filterChange1Min !== undefined || val.filterChange1Max !== undefined) {
    out.filters.change1 = {
      min: val.filterChange1Min,
      max: val.filterChange1Max,
    };
  }
  if (val.filterChange4Min !== undefined || val.filterChange4Max !== undefined) {
    out.filters.change4 = {
      min: val.filterChange4Min,
      max: val.filterChange4Max,
    };
  }
  if (val.filterChange12Min !== undefined || val.filterChange12Max !== undefined) {
    out.filters.change12 = {
      min: val.filterChange12Min,
      max: val.filterChange12Max,
    };
  }
  if (val.filterChange24Min !== undefined || val.filterChange24Max !== undefined) {
    out.filters.change24 = {
      min: val.filterChange24Min,
      max: val.filterChange24Max,
    };
  }

  return out;
}

export const discoveryPresets: Record<
  DiscoveryPreset,
  {
    sortBy: DiscoveryParametersSerialized['sortBy'];
    sortDirection: DiscoveryParametersSerialized['sortDirection'];
  }
> = {
  popular: { sortBy: 'volByTime', sortDirection: 'desc' },
  trending: { sortBy: 'trendingScore', sortDirection: 'desc' },
  currencies: { sortBy: 'marketCap', sortDirection: 'desc' },
  lsts: { sortBy: 'marketCap', sortDirection: 'desc' },
  social: { sortBy: 'volByTime', sortDirection: 'desc' },
  launchpads: { sortBy: 'marketCap', sortDirection: 'desc' },
  wrapped: { sortBy: 'marketCap', sortDirection: 'desc' },
  commodities: { sortBy: 'marketCap', sortDirection: 'desc' },
  stocks: { sortBy: 'marketCap', sortDirection: 'desc' },
};

export function serializeParams(
  params: DiscoveryParametersDeserialized,
): DiscoveryParametersSerialized {
  return {
    sortBy: params.sortBy,
    sortDirection: params.sortDirection,
    timeframe: params.timeframe,
    page: params.page,
    preset: params.preset,
    pageDiscovery: params.pageDiscovery ? 'true' : 'false',
    filterLiquidityMin: params.filters.liquidity?.min,
    filterLiquidityMax: params.filters.liquidity?.max,
    filterMarketCapMin: params.filters.marketCap?.min,
    filterMarketCapMax: params.filters.marketCap?.max,
    filterVolByTimeMin: params.filters.volByTime?.min,
    filterVolByTimeMax: params.filters.volByTime?.max,
    filterNumTxsByTimeMin: params.filters.numTxsByTime?.min,
    filterNumTxsByTimeMax: params.filters.numTxsByTime?.max,
    filterAgeMin: params.filters.age?.min,
    filterAgeMax: params.filters.age?.max,
    filterNumHoldersMin: params.filters.numHolders?.min,
    filterNumHoldersMax: params.filters.numHolders?.max,
    filterChange5mMin: params.filters.change5m?.min,
    filterChange5mMax: params.filters.change5m?.max,
    filterChange1Min: params.filters.change1?.min,
    filterChange1Max: params.filters.change1?.max,
    filterChange4Min: params.filters.change4?.min,
    filterChange4Max: params.filters.change4?.max,
    filterChange12Min: params.filters.change12?.min,
    filterChange12Max: params.filters.change12?.max,
    filterChange24Min: params.filters.change24?.min,
    filterChange24Max: params.filters.change24?.max,
    networks: params.filters.networks?.length ? params.filters.networks.join(',') : undefined,
    launchpads: params.filters.launchpads?.length ? params.filters.launchpads.join(',') : undefined,
    creator: params.filters.creator,
    currencies: params.filters.currencies?.length ? params.filters.currencies.join(',') : undefined,
    issuers: params.filters.issuers?.length ? params.filters.issuers.join(',') : undefined,
    backings: params.filters.backings?.length ? params.filters.backings.join(',') : undefined,
    searchQuery: params.searchQuery || undefined,
  };
}

export function isNumericFilter(obj: unknown): obj is DiscoveryNumericFilter {
  return typeof obj === 'object' && obj !== null && 'min' in obj && 'max' in obj;
}

export function getFieldName(
  fieldName: string,
  timeframe: z.infer<typeof zDiscoveryParameters>['timeframe'],
  isAggregatedPreset?: boolean,
) {
  if (fieldName === 'changeByTime') {
    return `change${timeframe}`;
  }
  if (fieldName === 'change1h') {
    return 'change1';
  }
  if (fieldName === 'change4h') {
    return 'change4';
  }
  if (fieldName === 'change12h') {
    return 'change12';
  }
  if (fieldName === 'change24h') {
    return 'change24';
  }
  if (fieldName === 'numTxsByTime') {
    return isAggregatedPreset ? `txncount${timeframe}` : `txnCount${timeframe}`;
  }
  if (fieldName === 'volByTime') {
    return `volume${timeframe}`;
  }
  if (fieldName === 'trendingScore') {
    return `trendingScore${timeframe}`;
  }
  if (fieldName === 'numHolders') {
    return 'holders';
  }
  if (fieldName === 'price') {
    return isAggregatedPreset ? 'price_usd' : 'priceUSD';
  }
  if (fieldName === 'age') {
    return isAggregatedPreset ? `created_at` : 'createdAt';
  }
  if (fieldName === 'marketCap') {
    return isAggregatedPreset ? 'market_cap' : 'marketCap';
  }
  if (fieldName === 'issuer') {
    return 'issuer_name';
  }
  return fieldName;
}

export function formatAge(createdAt?: number | null): string | null {
  if (createdAt == null || createdAt === 0) {
    return null;
  }

  const distanceInSeconds = differenceInSeconds(new Date(), new Date(createdAt * 1000));

  const seconds = Math.floor(distanceInSeconds);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30.42);
  const years = Math.floor(months / 12);

  if (years >= 1) {
    return `${years}y`;
  }
  if (months >= 1) {
    return `${months}mo`;
  }
  if (days >= 1) {
    return `${days}d`;
  }
  if (hours >= 1) {
    return `${hours}h`;
  }
  if (minutes >= 1) {
    return `${minutes}m`;
  }
  return `${seconds}s`;
}

export function getNumericFilters(
  params: { filters: DiscoveryFilters; timeframe: DiscoveryTimeframe },
  defaultParams: Record<string, number>,
  isAggregatedPreset?: boolean,
) {
  const filter: Record<string, unknown> = {};

  filter[getFieldName('liquidity', params.timeframe, isAggregatedPreset)] = {
    gte: defaultParams['discovery-minLiquidity'],
    lte: defaultParams['discovery-maxLiquidity'],
  };
  filter[getFieldName('marketCap', params.timeframe, isAggregatedPreset)] = {
    gte: defaultParams['discovery-minMarketCap'],
    lte: defaultParams['discovery-maxMarketCap'],
  };
  filter[getFieldName('volByTime', params.timeframe, isAggregatedPreset)] = {
    lte: defaultParams['discovery-maxVolume'],
  };
  filter[getFieldName('numHolders', params.timeframe, isAggregatedPreset)] = {
    gte: defaultParams['discovery-minHolders'],
  };
  filter[getFieldName('numTxsByTime', params.timeframe, isAggregatedPreset)] = {
    gte: defaultParams['discovery-minTxns'],
  };

  const getCreatedAtValue = (value?: number) => {
    if (!value) return undefined;
    const now = Date.now();
    const valueDate = subMinutes(now, value);

    return isAggregatedPreset ? valueDate : Math.floor(valueDate.getTime() / 1000);
  };

  for (const [key, value] of Object.entries(params.filters)) {
    if ((filterableNumericColumns as readonly string[]).includes(key) && isNumericFilter(value)) {
      const name = getFieldName(key, params.timeframe, isAggregatedPreset) as keyof typeof filter;
      if (key === 'age') {
        filter[name] = { gte: getCreatedAtValue(value.max), lte: getCreatedAtValue(value.min) };
      } else {
        filter[name] = { gte: value.min ?? undefined, lte: value.max ?? undefined };
      }
    }
  }

  return filter;
}
